import { Link, useNavigate } from "react-router-dom";
import { useResource } from "hooks/useResource";
import { getSections, getDepartments } from "services/content";

/**
 * Compact "Educational Journey" strip — three institution cards side by side
 * on a glass panel, joined by a golden road, designed to sit INSIDE the hero
 * slider so slider and journey are both visible without scrolling.
 *
 * No icons, no statistics (the school asked for neither): title with the short
 * form in brackets, a one-line subtitle, and on the honours card the department
 * chips (FNS / CDSR / FD) which link to /departments/<slug> by matching `code`.
 * Content: `sections` blob (admin → Sections); chips: `departments` blob.
 */

const hex = (h) => /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h || "");
const shade = (h, f) => {
  const m = hex(h);
  if (!m) return h;
  return "#" + m.slice(1).map((v) => Math.max(0, Math.round(parseInt(v, 16) * f)).toString(16).padStart(2, "0")).join("");
};
const tint = (h, f) => {
  const m = hex(h);
  if (!m) return h;
  return "#" + m.slice(1).map((v) => { const n = parseInt(v, 16); return Math.min(255, Math.round(n + (255 - n) * f)).toString(16).padStart(2, "0"); }).join("");
};

function Card({ item, departments, overlay }) {
  const navigate = useNavigate();
  const color = item.color || "#005C40";
  const solid = item.tone === "solid";
  const bg = solid
    ? `linear-gradient(160deg, ${tint(color, 0.18)} 0%, ${color} 55%, ${shade(color, 0.7)} 100%)`
    : "linear-gradient(160deg, #cfe3c8 0%, #a9c9a1 60%, #8fb587 100%)";
  const fg = solid ? "#fff" : "#123d2c";
  const sub = solid ? "rgba(255,255,255,0.78)" : "#2f5d43";

  const chips = (item.tags || [])
    .map((tag) => ({ tag, dept: departments.find((d) => d.code?.toLowerCase() === String(tag).toLowerCase() || d.slug === String(tag).toLowerCase()) }))
    .filter((c) => c.dept);

  // Desktop strip is a slim band roughly the height of the site header, so it
  // reads as a navigation bar rather than a block of cards (school request).
  // The stacked mobile version keeps the roomier sizing.
  const compact = overlay;

  const body = (
    <>
      {chips.length > 0 && (
        <div className={`flex justify-center ${compact ? "gap-1.5 mb-0.5" : "gap-2 mb-2"}`}>
          {chips.map(({ tag, dept }) => (
            <button
              key={tag}
              type="button"
              title={`${dept.name} — open department page`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/departments/${dept.slug}`);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className={`rounded-full font-bold text-white border-none cursor-pointer transition-transform hover:-translate-y-0.5 ${
                compact
                  ? "px-2.5 py-[2px] text-[10px] shadow-[0_2px_7px_rgba(0,0,0,0.25)]"
                  : "px-3 py-1 text-[11px] shadow-[0_3px_9px_rgba(0,0,0,0.25)]"
              }`}
              style={{ background: dept.color || shade(color, 0.7) }}
            >
              {tag}
            </button>
          ))}
        </div>
      )}
      <h3
        className={`font-serif font-bold leading-tight ${
          compact ? "text-[13px] xl:text-[14.5px]" : "text-[15px]"
        }`}
        style={{ color: fg }}
      >
        {item.title}
        {item.badge && <span className="font-normal"> ({item.badge})</span>}
      </h3>
      {item.desc && (
        <p className={compact ? "text-[10px] leading-tight mt-0" : "text-[11.5px] leading-snug mt-1"} style={{ color: sub }}>
          {item.desc}
        </p>
      )}
    </>
  );

  const cls =
    "relative flex flex-col justify-center no-underline text-center transition-transform duration-300 hover:-translate-y-1 " +
    (overlay
      ? "rounded-xl px-3 py-1.5 shadow-[0_10px_24px_rgba(0,0,0,0.32)]"
      : "rounded-2xl px-4 py-3 shadow-[0_8px_22px_rgba(20,30,20,0.15)]");
  const style = { background: bg, border: "1px solid rgba(255,255,255,0.35)" };

  return item.ctaHref ? (
    <Link to={item.ctaHref} className={cls} style={style}>{body}</Link>
  ) : (
    <div className={cls} style={style}>{body}</div>
  );
}

/** overlay=true: glass panel over the slider (lg+). overlay=false: plain stack (mobile). */
export default function JourneyStrip({ overlay = true }) {
  const { data } = useResource(getSections);
  const { data: deptData } = useResource(getDepartments);
  const items = data?.items || [];
  const departments = deptData?.items || [];
  if (!items.length) return null;

  if (!overlay) {
    // Stacked cards with a vertical golden road weaving down behind them.
    return (
      <div className="px-4 py-5 bg-[#0c2e22]">
        <div
          className="relative rounded-3xl p-3.5"
          style={{
            background: "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
            border: "1.5px solid rgba(229,191,110,0.7)",
          }}
        >
          <svg aria-hidden="true" className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 200 1000" preserveAspectRatio="none">
            <defs>
              <linearGradient id="strip-road-m" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#f2d89a" />
                <stop offset="50%" stopColor="#c99a3e" />
                <stop offset="100%" stopColor="#f2d89a" />
              </linearGradient>
              <filter id="strip-glow-m"><feGaussianBlur stdDeviation="6" /></filter>
            </defs>
            <path d="M 60 20 C 150 160, 40 330, 120 500 S 60 820, 140 980" fill="none" stroke="#c99a3e" strokeWidth="40" opacity="0.45" filter="url(#strip-glow-m)" />
            <path d="M 60 20 C 150 160, 40 330, 120 500 S 60 820, 140 980" fill="none" stroke="url(#strip-road-m)" strokeWidth="18" strokeLinecap="round" />
            <path d="M 60 20 C 150 160, 40 330, 120 500 S 60 820, 140 980" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <div className="relative flex flex-col gap-3.5">
            {items.map((it, i) => <Card key={`${it.badge}-${i}`} item={it} departments={departments} overlay={false} />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-container mx-auto px-6 sm:px-10">
      <div
        className="relative rounded-2xl p-2 xl:p-2.5 backdrop-blur-md"
        style={{
          background: "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.04))",
          border: "1.5px solid rgba(229,191,110,0.85)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.25)",
        }}
      >
        {/* golden road behind the cards */}
        <svg aria-hidden="true" className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1000 200" preserveAspectRatio="none">
          <defs>
            <linearGradient id="strip-road" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#f2d89a" />
              <stop offset="50%" stopColor="#c99a3e" />
              <stop offset="100%" stopColor="#f2d89a" />
            </linearGradient>
            <filter id="strip-glow"><feGaussianBlur stdDeviation="6" /></filter>
          </defs>
          <path d="M 40 130 C 200 60, 300 170, 500 100 S 800 40, 960 120" fill="none" stroke="#c99a3e" strokeWidth="40" opacity="0.45" filter="url(#strip-glow)" />
          <path d="M 40 130 C 200 60, 300 170, 500 100 S 800 40, 960 120" fill="none" stroke="url(#strip-road)" strokeWidth="20" strokeLinecap="round" />
          <path d="M 40 130 C 200 60, 300 170, 500 100 S 800 40, 960 120" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <div className="relative grid grid-cols-3 gap-2.5 xl:gap-3 items-stretch">
          {items.map((it, i) => <Card key={`${it.badge}-${i}`} item={it} departments={departments} overlay />)}
        </div>
      </div>
    </div>
  );
}
